import { Injectable } from '@angular/core';
import { API_SERVICES_CONFIG, format, PollingResourceConfig } from '@grid-ui/common';
import { PollingProgress, PollingService, PollItem } from '@grid-ui/extract';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, shareReplay, switchAll, take, takeWhile, tap } from 'rxjs/operators';
import { DataWizardExtract, DataWizardExtractService, DocumentLibraryService } from '../../../api-services';
import { mapPollStatusToDataWizardExtractErrorType, PollConfig, PollStatusEnum } from '../../../shared-models';
import { DataTableExtractRequest, NotificationBarEmbeddedNotification, NotificationBarNotificationType } from '../models';
import { NotificationBarService } from './notification-bar.service';

const POLL_STATUS_ERRORS: PollStatusEnum[] = [
  PollStatusEnum.Failed,
  PollStatusEnum.FailedDuplicate,
  PollStatusEnum.FailedScoringInProgress,
  PollStatusEnum.TimedOut,
  PollStatusEnum.ParameterRequired,
];

@Injectable()
export class CountryRiskDataTableExtractProcessHandlerService {
  /**
   * Long-lived, flatten stream of PollStatusEnum.
   * This Observable won't complete even after an extract
   * has finished.
   */
  public pollingProgress$: Observable<PollingProgress>;

  /**
   * Higher order Subject that emits an stream of PollConfig for
   * each new extract.
   */
  private pollStatusSource = new Subject<Observable<PollConfig | undefined>>();
  private showSideNavNotificationSource = new Subject<boolean>();
  private currentNotificationId: number | null = null;
  private currentPollServerId: number | undefined;
  private currentPollItem: PollItem | null = null;
  private extractPollingConfig: PollingResourceConfig;

  constructor(
    private readonly pollingService: PollingService,
    private readonly notificationBarService: NotificationBarService,
    private readonly dataWizardExtractService: DataWizardExtractService,
    private readonly documentLibraryService: DocumentLibraryService,
  ) {
    this.extractPollingConfig = API_SERVICES_CONFIG.v3.countryRisk.extract.table._configuration;

    this.pollingProgress$ = this.pollStatusSource.pipe(
      switchAll(),
      filter((pollConfig) => !!pollConfig),
      map<PollConfig, PollingProgress>((pollConfig) => ({ status: pollConfig.status, id: pollConfig.extractId })),
      shareReplay(1),
    );

    this.pollingProgress$.pipe(filter((x) => POLL_STATUS_ERRORS.includes(x.status))).subscribe((pollStatus: PollingProgress) => {
      const errorType = mapPollStatusToDataWizardExtractErrorType(pollStatus.status);
      if (errorType) {
        this.openErrorNotification();
      }
    });
  }

  public getExtractInProgressStatus(): Observable<boolean> {
    return this.pollingProgress$.pipe(
      map(
        (progress: PollingProgress | null) =>
          progress !== null && (progress.status === PollStatusEnum.Active || progress.status === PollStatusEnum.NotStarted),
      ),
    );
  }

  public getExtractDownloadUrl(): Observable<string> {
    const docsUrl = this.documentLibraryService.getDocumentsURLPathSegment('extracts');

    return this.pollingProgress$.pipe(
      filter((progress) => progress.status === PollStatusEnum.Completed),
      map((progress: PollingProgress) => (progress.id ? format(docsUrl, { id: progress.id }) : '')),
    );
  }

  public cancel(): void {
    this.cancelExtract();
  }

  public checkOngoingExtract(): Observable<PollingProgress | null> {
    if (this.currentPollItem) {
      return this.pollingProgress$;
    } else {
      return this.dataWizardExtractService.getInProgressExtract().pipe(
        take(1),
        catchError(() => of(null)),
        map((extract: DataWizardExtract | null) => {
          if (extract) {
            this.createNotification();
            this.currentPollServerId = extract.id;
            this.currentPollItem = this.pollingService.continueOngoingPoll(this.getResourceConfig(), this.currentPollServerId);
            this.pollStatusSource.next(this.currentPollItem.poll$);
            return this.pollingProgress$;
          }
          return of(null);
        }),
        switchAll(),
      );
    }
  }

  public closeNotifications(): void {
    this.cancelNotification();
    this.showSideNavNotificationSource.next(false);
  }

  public setSideNavNotificationOpenState(open: boolean): void {
    this.showSideNavNotificationSource.next(open);
  }

  public start(extractRequest: DataTableExtractRequest): Observable<PollingProgress> {
    this.createNotification();
    this.startNewPoll(extractRequest);

    return this.pollingProgress$;
  }

  private cancelExtract(): void {
    if (this.currentPollServerId) {
      this.pollingProgress$
        .pipe(
          take(1),
          map((x) => {
            if (!x.status || x.status === PollStatusEnum.Completed || !this.currentPollServerId) {
              return of(null);
            } else {
              return combineLatest([this.requestBackendExtractCancel(this.currentPollServerId), this.getCancelledPollStatus$()]);
            }
          }),
          switchAll(),
          take(1),
        )
        .subscribe(
          () => {},
          () => {
            this.openErrorNotification();
          },
        );
    }
  }

  private cancelNotification(): void {
    if (this.currentNotificationId) {
      this.notificationBarService.cancelNotification(this.currentNotificationId);
    }
  }

  private createNotification(): void {
    this.cancelNotification();
    this.currentNotificationId = this.notificationBarService.createNotification({
      type: NotificationBarNotificationType.embedded,
      embedded: NotificationBarEmbeddedNotification.dataTableExtract,
    });
  }

  private getCancelledPollStatus$(): Observable<PollingProgress | null> {
    return this.currentPollItem ? this.pollingProgress$.pipe(filter((x) => x.status === PollStatusEnum.Cancelled)) : of(null);
  }

  private getResourceConfig(): PollingResourceConfig {
    const pollingResourceConfig = this.extractPollingConfig;
    return pollingResourceConfig;
  }

  private openErrorNotification(): void {
    this.createNotification();
  }

  private requestBackendExtractCancel(pollServerId: number): Observable<DataWizardExtract> {
    return this.dataWizardExtractService.cancelExtract(pollServerId).pipe(
      tap(() => {
        this.cancelNotification();
        if (this.currentPollItem) {
          this.pollingService.cancel(this.currentPollItem.id);
        }
      }),
    );
  }

  private startNewPoll(extractRequest: DataTableExtractRequest): void {
    const pollingResourceConfig = this.getResourceConfig();
    this.currentPollItem = this.pollingService.start(pollingResourceConfig, extractRequest.body);

    this.currentPollItem.poll$
      .pipe(
        filter((pollConfig) => !!pollConfig),
        takeWhile<PollConfig>((pollConfig) => !pollConfig.serverPollingSessionId, true),
      )
      .subscribe((pollConfig) => {
        this.currentPollServerId = pollConfig.serverPollingSessionId;
      });

    this.pollStatusSource.next(this.currentPollItem.poll$);
    this.showSideNavNotificationSource.next(true);
  }
}
